<template lang="pug">
slide-drawer(
  v-if="slideDrawer"
  ref="sliderDrawerEmailSync"
  v-model="showPrompt"
  :confirm-dismiss="confirmDismiss"
  :default-cancel="false"
)
  template(slot="title")
    span {{prompt.title}}
  template(slot="content")
    v-container(align-content-center justify-center fill-height)
      v-layout.dialog-body.pa-5(column wrap align-center justify-center)
        v-flex.mb-3(shrink)
          p.slide-drawer-text(v-html="prompt.message")
        v-flex.action-btn.px-5(shrink)
          v-btn.primary(@click="handleConnectEmail" block flat) {{prompt.connectActionText}}
        v-flex(shrink)
          a.other-provider(:href="prompt.otherProviderLink") I want to connect to another email provider
        v-flex(shrink)
          v-checkbox(v-model="dontShowThisAgainCheckbox" label="Don't show this again")
liondesk-dialog-v2(
  v-else
  v-model="showPrompt"
  :dialog-title="prompt.title"
  cancel-button-text="Close"
  hide-ok-button
  hide-cancel-button
  close-button
  close-button-icon="$vuetify.icons.cancel-outline"
  @click:close="handleCloseDialog"
)
  template(slot="body")
    v-layout.dialog-body(column wrap align-center justify-center)
      v-flex
        p(v-html="prompt.message")
      v-flex.action-btn.px-5
        v-btn.primary(@click="handleConnectEmail" block flat) {{prompt.connectActionText}}
      v-flex
        a.other-provider(:href="prompt.otherProviderLink" @click="handleOtherProvider") I want to connect to another email provider
      v-flex
        v-checkbox(v-model="dontShowThisAgainCheckbox" label="Don't show this again")

</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import LiondeskDialogV2 from '@/Core/components/LiondeskDialogV2';
import SlideDrawer from '@/Core/components/SlideDrawer';

import {
  NO_SYNC_AUTH_DOMAIN,
  NO_SYNC_GMAIL,
  NO_PROMPT,
  NO_SYNC_PROMPT,
  HIDE_EMAIL_RECOMMEND_CONNECTION,
} from '../config/recommend-connection';

import { GOOGLE_DOMAINS } from '../config/email-management';

const {
  mapActions: mapEmailDomainAuthActions,
  mapGetters: mapEmailDomainAuthGetters,
  mapState: mapEmailDomainAuthState,
} = createNamespacedHelpers('emailDomainAuth');

const {
  mapActions: mapEmailManagementActions,
  mapGetters: mapEmailManagementGetters,
  mapState: mapEmailManagementState,
} = createNamespacedHelpers('emailManagement');

export default {
  name: 'recommend-email-sync',
  components: {
    LiondeskDialogV2,
    SlideDrawer,
  },
  props: {
    slideDrawer: Boolean
  },
  data() {
    return {
      showPrompt: false,
      confirmDismiss: false,
      dontShowThisAgainCheckbox: false,
    };
  },
  computed: {
    ...mapEmailDomainAuthState(['domainAuthInfo', 'systemDomain']),
    ...mapEmailDomainAuthGetters(['allowedNoAuthenticatedDomain', 'hasAuthenticatedDomain']),
    ...mapEmailManagementGetters(['hasAtLeastOneEmailAccount']),
    ...mapEmailManagementState(['emailAccounts']),
    isGmailDomain() {
      if (!this.domainAuthInfo) {
        return false;
      }
      const parts = this.domainAuthInfo.domain.split('.');
      return GOOGLE_DOMAINS.some(domain => domain === parts[0]);
    },
    prompt() {
      if (
        this.allowedNoAuthenticatedDomain &&
        !this.hasAuthenticatedDomain &&
        !this.hasAtLeastOneEmailAccount
      ) {
        return NO_SYNC_PROMPT[NO_SYNC_AUTH_DOMAIN];
      }

      if (
        !this.allowedNoAuthenticatedDomain &&
        !this.hasAtLeastOneEmailAccount &&
        this.isGmailDomain
      ) {
        return NO_SYNC_PROMPT[NO_SYNC_GMAIL];
      }
      return NO_SYNC_PROMPT[NO_PROMPT];
    },
  },
  methods: {
    ...mapEmailDomainAuthActions(['getAuthDomainInfo', 'getSystemDomain']),
    ...mapEmailManagementActions(['loadEmailAccounts']),
    async loadData() {
      const calls = [];

      if (!this.domainAuthInfo) {
        calls.push(this.getAuthDomainInfo());
      }

      if (!this.emailAccounts.length) {
        calls.push(this.loadEmailAccounts());
      }

      if (!this.systemDomain.domain) {
        calls.push(this.getSystemDomain());
      }

      await Promise.allSettled(calls);
    },
    loadLSHideDialog() {
      return localStorage.getItem(HIDE_EMAIL_RECOMMEND_CONNECTION) === 'true';
    },
    saveLSHideDialog(value) {
      localStorage.setItem(HIDE_EMAIL_RECOMMEND_CONNECTION, value);
    },
    shouldShowPrompt() {
      const authDomainCase =
        this.allowedNoAuthenticatedDomain &&
        !this.hasAuthenticatedDomain &&
        !this.hasAtLeastOneEmailAccount;

      const gmailCase =
        !this.allowedNoAuthenticatedDomain && !this.hasAtLeastOneEmailAccount && this.isGmailDomain;
      return (authDomainCase || gmailCase) && !this.loadLSHideDialog();
    },
    toggleShowProvider() {
      this.showPrompt = !this.showPrompt;
      this.confirmDismiss = this.showPrompt;
    },
    handleConnectEmail() {
      this.toggleShowProvider();
      this.$router.push(this.prompt.connectActionLink);
    },
    handleOtherProvider() {
      this.toggleShowProvider();
    },
    handleCloseDialog() {
      this.toggleShowProvider();
      this.$emit('click:close');
    },
  },
  watch: {
    dontShowThisAgainCheckbox(value) {
      this.saveLSHideDialog(value);
    },
  },
  async mounted() {
    await this.loadData();
    this.showPrompt = this.shouldShowPrompt();
    this.$emit('recommendSync', this.showPrompt);
  },
};
</script>

<style lang="scss" scoped>
.dialog-body {
  margin-bottom: -24px;
}
.other-provider {
  text-decoration: underline;
  color: black;
  font-size: 16px;
}
.slide-drawer-text {
  font-size: 18px
}
.action-btn {
  width: 100%;
}
</style>
